<script setup lang="ts">
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    ZhEmptyState,
    ZhPageTitle,
    ZhStatusPill,
} from '@zerohuman/ui'
import { onMounted, ref } from 'vue'
import { api, type StaffReconciliationRow } from '@/api'
import { dayOf } from '@/lib/staffEnterprises'

/**
 * The Staff area's reconciliation (#394, handed over from #448): every enterprise's usage recorded against what was
 * reported to FloPay, per closed period and unit, with where each stands. Only a platform admin is offered this page.
 */
const rows = ref<StaffReconciliationRow[]>([])
const loading = ref(true)
const error = ref<string | null>(null)

/** The period a row is for: the one it names, or its start and end days. */
function periodOf(row: StaffReconciliationRow): string {
    if (typeof row['period'] === 'string') {
        return row['period']
    }
    const start = dayOf(typeof row['periodStart'] === 'string' ? row['periodStart'] : null)
    const end = dayOf(typeof row['periodEnd'] === 'string' ? row['periodEnd'] : null)
    return end ? `${start} to ${end}` : start
}

/** A figure, or a dash where there is none yet (a report not yet sent). */
function figure(value: unknown): string {
    return typeof value === 'number' ? String(value) : '—'
}

onMounted(async () => {
    try {
        rows.value = await api.staffReconciliation()
    } catch (failure) {
        error.value = failure instanceof Error ? failure.message : 'Could not load the reconciliation.'
    } finally {
        loading.value = false
    }
})
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<ZhPageTitle kicker="Staff">Reconciliation</ZhPageTitle>
		<p class="text-muted-foreground text-sm">
			Each closed billing period and unit of every enterprise: the usage recorded, against what was reported to
			FloPay.
		</p>
		<p v-if="error" class="text-destructive text-sm" role="alert">{{ error }}</p>
		<Table v-if="rows.length">
			<TableHeader>
				<TableRow>
					<TableHead>Enterprise</TableHead>
					<TableHead>Period</TableHead>
					<TableHead>Unit</TableHead>
					<TableHead>Recorded</TableHead>
					<TableHead>Reported</TableHead>
					<TableHead>Status</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				<TableRow
					v-for="(row, index) in rows"
					:key="`${row.enterpriseId}-${periodOf(row)}-${row.unit}-${index}`"
					data-testid="staff-reconciliation-row"
				>
					<TableCell><code>{{ row.enterpriseId }}</code></TableCell>
					<TableCell>{{ periodOf(row) }}</TableCell>
					<TableCell>{{ row.unit }}</TableCell>
					<TableCell>{{ figure(row['recorded']) }}</TableCell>
					<TableCell>{{ figure(row['reported']) }}</TableCell>
					<TableCell>
						<ZhStatusPill :tone="row.status === 'pending' || row.status === 'mismatch' ? 'action' : 'neutral'" :pulse="false">
							{{ row.status }}
						</ZhStatusPill>
					</TableCell>
				</TableRow>
			</TableBody>
		</Table>
		<ZhEmptyState v-else-if="!loading && !error">No closed periods to reconcile yet.</ZhEmptyState>
	</section>
</template>
